import * as React from 'react';

const normalizeValue = (value: number) => Math.max(0, value);

export const useOverflowBox = (
  ref: React.RefObject<HTMLElement | undefined>,
) => {
  const [overflowBox, setOverflowBox] = React.useState({
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  });

  const updateOverflowBox = React.useCallback(() => {
    if (ref?.current) {
      const box = ref.current;
      setOverflowBox({
        top: normalizeValue(Math.floor(box.scrollTop)),
        right: normalizeValue(
          Math.floor(box.scrollWidth - box.clientWidth - box.scrollLeft),
        ),
        bottom: normalizeValue(
          Math.floor(box.scrollHeight - box.clientHeight - box.scrollTop),
        ),
        left: normalizeValue(Math.floor(box.scrollLeft)),
      });
    }
  }, [ref]);

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    updateOverflowBox();

    const ro = new ResizeObserver(() => {
      updateOverflowBox();
    });

    const observeScrollContent = () => {
      ro.observe(el);
      for (const child of el.children) {
        if (child instanceof HTMLElement) {
          ro.observe(child);
        }
      }
    };

    observeScrollContent();

    const mo = new MutationObserver(() => {
      ro.disconnect();
      observeScrollContent();
      updateOverflowBox();
    });

    mo.observe(el, { childList: true });

    const postLayoutRaf = requestAnimationFrame(() => {
      updateOverflowBox();
    });

    return () => {
      cancelAnimationFrame(postLayoutRaf);
      mo.disconnect();
      ro.disconnect();
    };
  }, [ref, updateOverflowBox]);

  const hasOverflow =
    overflowBox.top ||
    overflowBox.bottom ||
    overflowBox.left ||
    overflowBox.right;

  return {
    updateOverflowBox,
    hasOverflow,
    overflowBox,
  };
};
